import { CatalogResourceDc, ServiceInfoDc } from "@evergis/api";
import { IJSXOption, IOption, useToggle } from "@evergis/ui";
import React, { FC, useCallback, useMemo, useState } from "react";
import { useSelectFilesDialog } from "../../hooks/useDialogs";
import { Catalog } from "../../modules/Catalog/components/Catalog";
import { CatalogButton, LayersDropdown, LayersDropdownContainer } from "../LayersSelector/styled";
import { ResourceSelector } from "./ResourceSelector";
import { ResourcesSelectorDropdownProps } from "./types";

const filterOptions = (options: IOption[], reserved: string[]) =>
  (options && Array.isArray(options) && options.filter(option => !reserved.includes(option.value))) || [];

export const ResourcesSelectorDropdown: FC<ResourcesSelectorDropdownProps> = ({
  resourceName,
  hiddenResources,
  withCatalog,
  withResourceCatalog,
  resourceCatalogFilters,
  resourceOptions,
  loading,
  resourceType,
  onChange,
  ...props
}) => {
  const [catalogVisible, toggleCatalogVisible] = useToggle(false);
  const [selectedResourcesFromCatalog, setSelectedResourcesFromCatalog] = useState<Partial<ServiceInfoDc>[]>([]);

  const handleChange = useCallback(
    ([option]: IJSXOption[]) => {
      onChange({
        text: `${option.text}`,
        value: option.value,
      });
    },
    [onChange],
  );

  const selectFromCatalog = useCallback(
    (resource: ServiceInfoDc) => {
      if (resourceOptions.findIndex(({ value }) => value === resource.name) === -1) {
        setSelectedResourcesFromCatalog(acc => [...acc, resource]);
      }

      onChange({
        text: resource.alias || resource.name,
        value: resource.name,
      });
      toggleCatalogVisible();
    },
    [onChange, toggleCatalogVisible, resourceOptions],
  );

  const selectResourcesFromCatalog = useCallback(
    (selectedResources: CatalogResourceDc[]) => {
      const resource = selectedResources[0];

      if (resourceOptions.findIndex(({ value }) => value === resource.systemName) === -1) {
        setSelectedResourcesFromCatalog(acc => [...acc, { name: resource.systemName, alias: resource.name }]);
      }

      onChange({
        text: resource.name,
        value: resource.systemName,
      });
    },
    [onChange, resourceOptions],
  );

  const openResourceCatalogDialog = useSelectFilesDialog();

  const openResourceCatalog = useCallback(() => {
    openResourceCatalogDialog([""], {
      onApply: selectResourcesFromCatalog,
      hiddenItems: [resourceName],
      filters: resourceCatalogFilters,
      hasActions: false,
    });
  }, [openResourceCatalogDialog, selectResourcesFromCatalog, resourceName, resourceCatalogFilters]);

  const options = useMemo(() => {
    const resultOptions =
      hiddenResources && hiddenResources.length ? filterOptions(resourceOptions, hiddenResources) : resourceOptions;
    const catalogOptions = selectedResourcesFromCatalog.map(({ name, alias }) => ({
      text: alias || name,
      value: name,
    }));

    return [...resultOptions, ...catalogOptions];
  }, [hiddenResources, resourceOptions, selectedResourcesFromCatalog]);

  return (
    <LayersDropdownContainer>
      <LayersDropdown
        value={resourceName}
        loading={loading}
        options={options}
        onChange={handleChange}
        withCatalog={withCatalog}
        {...props}
      />
      {(withCatalog || withResourceCatalog) && (
        <CatalogButton
          zIndex={props.zIndex}
          kind="folder"
          onClick={withResourceCatalog ? openResourceCatalog : toggleCatalogVisible}
        />
      )}
      {withCatalog && (
        <ResourceSelector
          resourceType={resourceType}
          active={catalogVisible}
          selectedResourceName={resourceName}
          onClose={toggleCatalogVisible}
          zIndex={props.zIndex + 1}
          onSelect={selectFromCatalog}
        >
          <Catalog />
        </ResourceSelector>
      )}
    </LayersDropdownContainer>
  );
};
